import type React from "react"

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Load } from "components/load"
import PageError from "components/page-error"
import SingleDatePicker from "components/SingleDatePicker"
import { format } from "date-fns"
import type { Meta } from "src/@types/paginated"

import { JobProgressInline } from "@/components/job-progress-inline"
import { JobProgressProvider } from "@/context/job-progress-context"
import { RoleEnum } from "@/enum/role"
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage"
import { useGetStoresConciliationsQuery } from "@/services/store-conciliation"
import { useGetUserStoresQuery } from "@/services/store/store"
import { layoutClasses } from "@/utils/css-classes"
import { getPreviousDayDate } from "@/utils/dates"
import { formatFilterValue } from "@/utils/formatters"
import { handeOrderBy } from "@/utils/pagination"
import type { ExtendedColumnDef } from "components/Datatable"
import { PageLayout, PageTitle, ReportFooter } from "components/layout"
import { Button } from "components/ui/button"
import { RotateCcw, Users2 } from "lucide-react"
import { useNavigate } from "react-router-dom"
import type { IPayment, IStoreColumns } from "src/@types/stores"
import { useFilterActions } from "../ducks/filter/hooks/actions"
import { useFilterState } from "../ducks/filter/hooks/filterState"
import { columns } from "./columns"
import AlertModal from "./components/alert-modal"
import ExportOptions from "./components/export-options"
import { ReprocessStoresModal } from "./components/reprocess-modal"
import SelectFilters from "./components/select-filters"
import Table from "./components/table"
import { CONCILIATION_TAB_GROUPS, conciliationTabGroups } from "@/config/routes/conciliation/tab-groups"
import { CONCILIATION_TAB_KEYS } from "@/config/routes/conciliation/tab-keys"

const ANALYST_ASSIGNMENT_PATH: string = conciliationTabGroups.tabUrl(
  CONCILIATION_TAB_GROUPS.CONFIGURACOES,
  CONCILIATION_TAB_KEYS.CONFIGURACOES.ATRIBUICAO_DE_ANALISTA,
)

export const StoresPage: React.FC = () => {
  const { updateFilter } = useFilterActions()
  const filterState = useFilterState()
  const { filters } = filterState || { filters: {} }

  const navigate = useNavigate()

  const normalizeDateString = (value: unknown): string => {
    if (typeof value === "string") return value.split("T")[0]
    if (value instanceof Date) return format(value, "yyyy-MM-dd")
    return format(new Date(), "yyyy-MM-dd")
  }

  const getFilters = () => ({
    ...(filters.date && {
      transactionDate: normalizeDateString(filters.date),
    }),
    ...(filters.code && { code: formatFilterValue(filters.code) }),
    ...(filters.responsible && { responsible: formatFilterValue(filters.responsible) }),
    ...(filters.status && { status: formatFilterValue(filters.status) }),
    ...(filters.regional_id && { regional_id: formatFilterValue(filters.regional_id) }),
    ...(filters.divergence_value_range && {
      divergence_value_range: formatFilterValue(filters.divergence_value_range),
    }),
    ...(filters.available_to_close && {
      available_to_close: filters.available_to_close,
    }),
    is_store_listing: true,
  })

  const {
    data: res,
    isLoading,
    refetch,
    isFetching,
    error,
  } = useGetStoresConciliationsQuery(
    {
      ...getFilters(),
      orderBy: JSON.stringify(handeOrderBy(filters.order as any)),
      perPage: 13,
      page: filters.meta?.page || 1,
    },
    {
      refetchOnFocus: false,
    },
  )

  const { data, meta } = res ?? { data: [], meta: {} }

  const handleDatePickerChange = (date: Date | null) => {
    updateFilter({ key: "date", value: date ? format(date, "yyyy-MM-dd") : null })
  }

  const user = getUserFromLocalStorage()
  const moduleAccess = localStorage.getItem("@verzel:module")

  const group = user?.user_permission_group?.find((item: any) => item.group === moduleAccess)
  const handleRedirect = () => {
    navigate(ANALYST_ASSIGNMENT_PATH)
  }

  const { data: userStores } = useGetUserStoresQuery({ id: user?.id }, { skip: !user?.id })

  if (error) {
    return <PageError status={500} message={"ERRO INTERNO NO SERVIDOR"} />
  }

  if ((isLoading || isFetching) && (!data || data.length === 0)) {
    return <Load />
  }

  return (
    <JobProgressProvider>
      <PageLayout>
        {!userStores ? <AlertModal /> : null}
        <div className={layoutClasses.pageContainer}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-6">
            <div className="flex-1">
              <PageTitle
                title={"Lista de Lojas"}
                description="Conciliação de Caixa"
                titleSize="text-3xl"
                descriptionSize="text-xl"
              />
            </div>
            <div className="cursor-pointer">
              <SingleDatePicker
                date={
                  filters.date
                    ? new Date(normalizeDateString(filters.date) + "T12:00:00")
                    : null
                }
                onDateChange={handleDatePickerChange}
                placeholder="Data de conciliação"
                maxDate={getPreviousDayDate(new Date())}
              />
            </div>
          </div>
          <JobProgressInline />
          <div className={layoutClasses.contentWrapper}>
            <div className="p-0">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex-wrap">
                    <SelectFilters />
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="flex items-center gap-2 min-w-[120px] border-[#2B0C1A] text-[#2B0C1A] hover:text-[#fff]"
                >
                  <RotateCcw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
                  {isFetching ? "Carregando..." : "Recarregar"}
                </Button>
              </div>
            </div>

            <div className={layoutClasses.tableContainer}>
              <div className={layoutClasses.tableWrapper}>
                <Table
                  data={data as unknown as IPayment[]}
                  columns={columns as ExtendedColumnDef<IStoreColumns>[]}
                  isFetching={isFetching}
                  meta={meta as Meta}
                />
              </div>
            </div>
          </div>

          <ReportFooter>
            <ExportOptions filters={filters} />

            {group?.role.includes(RoleEnum.ADMINISTRADOR) && (
              <div className="flex items-center gap-4">
                <div className="hidden lg:block w-px h-8 bg-[#2B0C1A]" />
                <div className="flex items-center gap-3">
                  <ReprocessStoresModal conciliationDate={filters.date as string} />

                  <Button
                    onClick={handleRedirect}
                    variant="default"
                    className="gap-2"
                  >
                    <Users2 className="w-4 h-4" />
                    Loja x Responsável
                  </Button>
                </div>
              </div>
            )}
          </ReportFooter>
        </div>
      </PageLayout>
    </JobProgressProvider>
  )
}
